import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { ModalDismissReasons, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, takeUntil } from 'rxjs';
import {
  SaleRequestsTableFields,
  SaleTabs,
} from 'src/app/models/sales/sales.model';
import { ActivatedRoute } from '@angular/router';
import { ToastService } from 'src/app/shared-new/services/toast.service';
import { SaleMemberRequests, SaleMembersStatusIds } from '../../models/sales/sale-member-request.model';
import { ReviewVetProfilePopupComponent } from '../../profile-page/review-vet-profile-popup/review-vet-profile-popup.component';
import { HttpErrorHandlerService } from '../../services/http-error-handler.service';
import { SalesService } from '../../services/sales.service';
import { BaseUnsubscribeComponent } from '../../shared-new/components/base-unsubscribe/base-unsubscribe.component';
import { SaleMemberStatus } from '../../shared-new/enums/sale-member-status.enum';
import { SaleMemberRequestPopupComponent } from '../sale-member-request-popup/sale-member-request-popup.component';
import { SaleRequestsDataSource } from './sale-requests-datasource';
@Component({
  selector: 'app-sale-requests',
  templateUrl: './sale-requests.component.html',
  styleUrls: ['./sale-requests.component.scss'],
})
export class SaleRequestsComponent
  extends BaseUnsubscribeComponent
  implements OnInit {
  @Input() public saleId: number;
  public requestTab: string = 'requested';
  public saleTabs = SaleTabs;
  public saleRequestsTableFields = SaleRequestsTableFields;
  public closeResult = '';
  public isDataBindAfterApproval = false;
  public dataSource: SaleRequestsDataSource;
  public displayedColumns$ = new BehaviorSubject([
    'memberName',
    'clinic',
    'emailAddress',
    'mobile',
    'createdDate',
    'access',
    'status',
    'comments',
    'action',
  ]);

  isLoader = false;
  @Output() approveRequest: EventEmitter<any> = new EventEmitter();
  @Output() deniedRequest: EventEmitter<any> = new EventEmitter();
  @ViewChild(MatSort) requestSort: MatSort;
  constructor(
    private readonly saleService: SalesService,
    private readonly modalService: NgbModal,
    private readonly activatedRoute: ActivatedRoute,
    private toastService: ToastService,
    private readonly httpErrorHandlerService: HttpErrorHandlerService
  ) {
    super();
  }

  public getRequestedCount(): number {
    const requested = this._saleMemberRequests.filter(
      (x) => x.saleMemberStatusId == SaleMembersStatusIds.REQUESTED
    );
    return requested.length;
  }

  trackByGroup(index: number, el: SaleMemberRequests): number {
    return el.saleMemberRequestId;
  }

  public onTableScroll(): void {
    this.dataSource.onScroll();
  }

  public getApprovedCount(): number {
    const approved = this._saleMemberRequests.filter(
      (x) => x.saleMemberStatusId == SaleMembersStatusIds.APPROVED || x.saleMemberStatusId == SaleMembersStatusIds.APPROVED_SECOND_STATUS_ID
    );
    return approved.length;
  }
  public getDeniedCount(): number {
    const denied = this._saleMemberRequests.filter(
      (x) => x.saleMemberStatusId == SaleMembersStatusIds.DENIED
    );
    return denied.length;
  }
  public getInvitedCount(): number {
    const invited = this._saleMemberRequests.filter(
      (x) => x.saleMemberStatusId == SaleMembersStatusIds.INVITED
    );
    return invited.length;
  }
  public ngAfterViewInit(): void {
    window.setTimeout(() => {
      this.activatedRoute.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
        const vetMemberId = +params['vetmemberid'];
        const userId = +params['userid'];
        if (vetMemberId && userId) {
          this.openRequestFromLink(vetMemberId, userId);
        }
      });
    }, 500);
  }

  /**
   * "Review Access Request" email link (/sales/{id}?vetmemberid=&userid=&tab=requests). The link carries no
   * request id, so the real PENDING request is looked up for this sale + vet member + user before Approve/Deny
   * are offered. If there is none (already decided, withdrawn, wrong sale) the popup opens read-only.
   */
  private openRequestFromLink(vetMemberId: number, userId: number): void {
    this.saleService
      .getSaleMemberRequests(this.saleId, 0)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (requests) => {
          const pending = SaleRequestsComponent.findPendingRequest(requests, vetMemberId, userId);
          if (pending) {
            this.ReviewVetInfo(pending);
          } else {
            const request = new SaleMemberRequests();
            request.memberId = vetMemberId;
            request.userId = userId;
            this.ReviewVetInfo(request, true);
          }
        },
        error: () => {
          const request = new SaleMemberRequests();
          request.memberId = vetMemberId;
          request.userId = userId;
          this.ReviewVetInfo(request, true);
        }
      });
  }

  /** The newest REQUESTED row for this vet member and user (a row without a user id matches on member alone). */
  public static findPendingRequest(requests: SaleMemberRequests[] | null | undefined, vetMemberId: number, userId: number): SaleMemberRequests | null {
    const pending = (requests || []).filter(
      (r) => r && r.saleMemberRequestId > 0 && r.memberId == vetMemberId && r.saleMemberStatusId == SaleMembersStatusIds.REQUESTED
    );
    const sameUser = pending.filter((r) => r.userId == userId);
    const candidates = sameUser.length ? sameUser : pending.filter((r) => !r.userId);
    if (!candidates.length) {
      return null;
    }
    return candidates.reduce((a, b) => (b.saleMemberRequestId > a.saleMemberRequestId ? b : a));
  }

  public ReviewVetInfo(request: SaleMemberRequests, requestUnavailable: boolean = false) {
    const modalRef = this.modalService.open(ReviewVetProfilePopupComponent, {
      ariaLabelledBy: this.saleId + 'Id',
      centered: true,
      size: '50-width',
      keyboard: false,
      backdrop: 'static',

    });

    modalRef.componentInstance.data = {
      memberId: request.memberId,
      userId: request.userId,
      saleId: this.saleId,
      saleMemberRequestId: request.saleMemberRequestId,
      requestUnavailable: requestUnavailable || !(request.saleMemberRequestId > 0)
    };
    modalRef.result.then(
      (result) => {
        const data = JSON.parse(result);
        if ('isApproved' in data) {
          if (!(request.saleMemberRequestId > 0)) {
            // Never send a decision without the real request (the API refuses it; it used to email the vet anyway).
            this.toastService.error(ReviewVetProfilePopupComponent.REQUEST_UNAVAILABLE_MESSAGE, 'Error!');
            return;
          }
          if (data.isApproved) {
            this.ApproveRequest(request, data.comments, data.notificationData, data.isSubmitAccess, data.isViewAccess, data.isAllSaleAccess);
          } else {
            this.DeniedRequest(request, data.comments, data.notificationData);
          }
        }
      },
      (reason) => {
        this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
      }
    );
  }
  public ApproveRequest(request: SaleMemberRequests, comments: string, notificationData: any, isSubmitAccess: boolean, isViewAccess: boolean, isAllSaleAccess: boolean) {
    this.isLoader = true;
    const acceptRequest = {
      saleMemberRequestId: request.saleMemberRequestId,
      saleMemberStatusId: SaleMemberStatus.Approved,
      comments: comments,
      isSubmitAccess: isSubmitAccess,
      isViewAccess: isViewAccess,
      isAllSaleAccess: isAllSaleAccess,
      notification: notificationData
    };
    this.saleService
      .acceptSaleMemberRequestAccess(acceptRequest)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.isLoader = false;
          this.toastService.success('Member request has been approved');
          this.isDataBindAfterApproval = true;
          //this._saleMemberRequests = response;
          //this.onClickTab(this.requestTab);
          this.approveRequest.next(response);
        },
        error: (error) => {
          this.isLoader = false;
          this.httpErrorHandlerService.ShowCustomErrorMessage(error, 'Request not approved');
        }
      });
  }
  public DeniedRequest(request: SaleMemberRequests, comments: string, notificationData: any) {
    this.isLoader = true;
    const deniedRequest = {
      saleMemberRequestId: request.saleMemberRequestId,
      saleMemberStatusId: SaleMemberStatus.Denied,
      comments: comments,
      notification: notificationData
    };
    this.saleService
      .acceptSaleMemberRequestAccess(deniedRequest)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.isLoader = false;
          this.toastService.success('Member request has been denied');
          // this._saleMemberRequests = response;
          //this.onClickTab(this.requestTab);
          this.deniedRequest.next(response);
        },
        error: (error) => {
          this.isLoader = false;
          this.httpErrorHandlerService.ShowCustomErrorMessage(error, 'Request not denied');
        }
      });
  }
  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }
  public load(statusId: number): void {
    if (!this._saleMemberRequests) {
      return;
    }
    const data =
      statusId === 0
        ? this._saleMemberRequests
        : statusId === 3
          ? this._saleMemberRequests.filter(
            (x) =>
              x.saleMemberStatusId == statusId || x.saleMemberStatusId == -2
          )
          : this._saleMemberRequests.filter(
            (x) => x.saleMemberStatusId == statusId
          );
    setTimeout(() => {
      if (this.requestSort) {
        this.dataSource = new SaleRequestsDataSource(data, this.requestSort);
        this.dataSource.applyDefaultSort();
        this.dataSource.searchTable(this._searchTerm);
      }
    }, 100);
  }
  public onClickTab(tab: string) {
    this.requestTab = tab;
    if ('all' === tab) {
      this.load(0);
    }
    if ('requested' === tab) {
      this.load(5);
    }
    if ('denied' === tab) {
      this.load(4);
    }
    if ('approved' === tab) {
      this.load(3);
    }
    if ('invited' === tab) {
      this.load(-1);
    }
  }
  public getAccess(saleRequest: SaleMemberRequests): string {
    if (saleRequest.isSubmitAccess && saleRequest.isViewAccess) {
      return 'View & Submit';
    }
    let access = '';
    if (saleRequest.isSubmitAccess) {
      access = 'Submit';
    }
    if (saleRequest.isViewAccess) {
      access = 'View';
    }
    return access;
  }
  public ngOnInit(): void { }

  public _tab: string;
  @Input() set tab(value: string) {
    this._tab = value;
    this.requestTab = 'requested';
    if (value === this.saleTabs.REQUESTS) {
      this.load(5);
    }
  }

  public _saleMemberRequests: SaleMemberRequests[];
  @Input() set saleMemberRequests(value: SaleMemberRequests[]) {
    this._saleMemberRequests = value;

    if (typeof value !== 'undefined') {
      this.onClickTab(this.requestTab);
    }
  }

  private _searchTerm: string;
  @Input() set searchTerm(value: string) {
    this._searchTerm = value;
    if (!this.dataSource) {
      this.load(5);
    }
    this.dataSource?.searchTable(value);
  }




  public override ngOnDestroy(): void {
    this.unsubscribe$.next();
  }


}
